<template>
  <div class="min-h-screen overflow-auto overflow-y-auto bg-gray-50">
    <!-- Loading State -->
    <div v-if="loading" class="tw-flex items-center justify-center h-64">
      <LoadingNew/>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="tw-flex items-center justify-center h-64">
      <div class="text-center">
        <Icon name="exclamation-circle" class="w-12 h-12 text-red-400 mx-auto mb-4"/>
        <h3 class="text-lg font-semibold text-gray-900 mb-2">{{ $t('knowledge_base_document_not_found') }}</h3>
        <p class="text-gray-500 mb-4">{{ error }}</p>
        <ds-button :label="$t('knowledge_base_back_to_knowledge_base')" @click="goBack" variant="secondary"/>
      </div>
    </div>

    <!-- Document Content -->
    <div v-else-if="article">
      <!-- Hero Section -->
      <div class="relative overflow-hidden bg-gradient-to-br from-blue-50 to-gray-50">
        <div class="max-w-7xl mx-auto px-4 py-12">
          <div class="grid gap-8 lg:grid-cols-2 lg:gap-12">
            <div class="tw-flex flex-col justify-center space-y-6">
              <div class="space-y-4">
                <div
                  class="hover:underline cursor-pointer"
                  @click="$router.go(-1)"
                >
                  <Icon name="arrow-left" size="14"/>
                  {{ $t('go_back') }}
                </div>
                <div class="tw-flex items-center gap-2">
                  <span
                    v-if="article.category"
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                  >
                    {{ article.category }}
                  </span>
                </div>

                <h1 class="text-2xl font-bold tracking-tight text-gray-900 lg:text-3xl">
                  {{ article.title }}
                </h1>

                <p v-if="article.excerpt" class="text-base text-gray-600">
                  {{ article.excerpt }}
                </p>
              </div>

              <div class="tw-flex flex-wrap items-center gap-4 text-sm text-gray-500 justify-between">
                <div v-if="article.author" class="tw-flex items-center gap-1">
                  <!--                  <Icon name="user" class="h-4 w-4"/>
                                    <span>{{ article.author.name }}</span>-->
                  <AvatarNew :user="article.author" show-details/>
                </div>
                <!--                <div v-if="article.updated_at" class="tw-flex items-center gap-1">
                                  <Icon name="clock" class="h-4 w-4"/>
                                  <span>Updated {{ formatDate(article.updated_at) }}</span>
                                </div>-->
                <div v-if="files.length > 0" class="tw-flex items-center gap-1">
                  <Icon name="file-text" class="h-2 w-2"/>
                  <span>{{
                      files.length
                    }} {{
                      files.length === 1 ? $t('knowledge_base_files_attached_single') : $t('knowledge_base_files_attached_multiple')
                    }}</span>
                </div>
              </div>
            </div>

            <div v-if="article.featured_image" class="relative">
              <!-- Disable right click "save image" -->
              <div class="absolute z-2 bg-transparent w-full h-full"></div>
              <img
                :src="article.featured_image"
                :alt="article.title"
                class="rounded-lg shadow-lg w-full object-cover z-1"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="max-w-7xl mx-auto px-4 py-8">
        <div
          class="grid gap-8 lg:grid-cols-3"
          v-if="canViewAllKnowledgeBaseContentTypes || canViewDetailKnowledgeBaseContentTypes"
        >
          <!-- Main Content -->
          <div class="lg:col-span-2 space-y-8">
            <!-- Description -->
            <div class="bg-white rounded shadow-sm border">
              <div class="p-4 border-b">
                <h2 class="text-base font-semibold text-gray-900 uppercase">{{
                    $t('knowledge_base_overview_header')
                  }}</h2>
                <p class="text-sm text-gray-600 mt-1">
                  {{ $t('knowledge_base_detailed_information_about_this_post') }}
                </p>
              </div>
              <div class="p-4">
                <!-- Content visible to logged-in users -->
                <div>
                  <div v-if="article.body" class="prose prose-sm max-w-none text-gray-700" v-html="article.body"></div>
                  <p v-else class="text-gray-500 italic">{{ $t('knowledge_base_no_description_available') }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Sidebar -->
          <div class="space-y-4">

            <!-- Files Section -->
            <div class="bg-white rounded shadow-sm border">
              <div class="p-4 border-b">
                <h3 class="text-base font-semibold text-gray-900 uppercase">
                  {{ $t('knowledge_base_attached_files_header') }}</h3>
                <p class="text-sm text-gray-600 mt-1">
                  {{ $t('knowledge_base_documents_media_and_resources_related_to_this_post') }}
                </p>
              </div>
              <div class="p-4 space-y-4">
                <!-- Files visible to logged-in users -->
                <div v-for="file in files" :key="file.id" class="rounded-md overflow-hidden mb-2">
                  <!-- YouTube Embed -->
                  <div v-if="isYouTubeUrl(file.url)" class="space-y-4 border rounded-lg">
                    <div class="aspect-w-16 h-36">
                      <iframe
                        :src="getYouTubeEmbedUrl(file.url)"
                        title="YouTube video player"
                        frameborder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowfullscreen
                        class="w-full h-40"
                      ></iframe>
                    </div>
                    <div class="p-4">
                      <div class="tw-flex items-center space-x-3 mb-3">
                        <div class="w-10 h-10 rounded-lg tw-flex items-center justify-center">
                          <img
                            :src="getClearbitLogoUrl(file.url)"
                            :alt="file.name || 'Website'"
                            class="w-8 h-8 object-contain inset-0 m-auto"
                          />
                        </div>
                        <div class="flex-1 min-w-0">
                          <div class="tw-flex items-center gap-2 mb-1 justify-between">
                            <h4 class="text-sm font-medium text-gray-900 truncate">
                              {{ file.name || $t('knowledge_base_youtube_video') }}</h4>
                            <span
                              v-if="file.is_featured"
                              class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 flex-shrink-0"
                            >
                              {{ $t('knowledge_base_featured') }}
                            </span>
                          </div>
                          <p class="text-xs text-gray-500">{{ $t('knowledge_base_youtube_video_label') }}</p>
                        </div>
                      </div>
                      <a
                        :href="file.url"
                        target="_blank"
                        class="tw-flex items-center space-x-1 text-sm text-red-600 hover:text-red-800"
                      >
                        <Icon name="external-link" class="w-4 h-4"/>
                        <span>{{ $t('knowledge_base_view_on_youtube') }}</span>
                      </a>
                    </div>
                  </div>

                  <!-- Spotify Embed -->
                  <div v-else-if="isSpotifyUrl(file.url)" class="border rounded-xl space-y-4">
                    <iframe
                      :src="getSpotifyEmbedUrl(file.url)"
                      width="100%"
                      height="80"
                      frameborder="0"
                      allowtransparency="true"
                      allow="encrypted-media"
                      class="rounded-t-sm"
                    ></iframe>
                    <div class="p-4">
                      <div class="tw-flex items-center space-x-3 mb-3">
                        <div class="w-10 h-10 rounded-lg tw-flex items-center justify-center">
                          <!--                          <Icon name="musical-note" class="w-5 h-5 text-white"/>-->
                          <img
                            :src="getClearbitLogoUrl(file.url)"
                            :alt="file.name || 'Website'"
                            class="w-8 h-8 object-contain inset-0 m-auto"
                            @error="$event.target.style.display='none'"
                          />
                        </div>
                        <div class="flex-1 min-w-0">
                          <div class="tw-flex items-center gap-2 mb-1 justify-between">
                            <h4 class="text-sm font-medium text-gray-900 truncate">{{
                                file.name || $t('knowledge_base_spotify_content')
                              }}</h4>
                            <span
                              v-if="file.is_featured"
                              class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 flex-shrink-0"
                            >
                              {{ $t('knowledge_base_featured') }}
                            </span>
                          </div>
                          <p class="text-xs text-gray-500">{{ $t('knowledge_base_spotify_label') }}</p>
                        </div>
                      </div>
                      <a
                        :href="file.url"
                        target="_blank"
                        class="tw-flex items-center space-x-1 text-sm text-green-600 hover:text-green-800"
                      >
                        <Icon name="external-link" class="w-4 h-4"/>
                        <span>{{ $t('knowledge_base_open_in_spotify') }}</span>
                      </a>
                    </div>
                  </div>

                  <!-- Regular URL with Clearbit Logo -->
                  <div v-else-if="isRegularUrl(file)" class="p-4">
                    <div class="tw-flex items-center space-x-3 mb-3">
                      <div
                        class="w-10 h-10 bg-gray-100 rounded-lg tw-flex items-center justify-center overflow-hidden relative"
                      >
                        <!--                        <Icon
                                                  name="link"
                                                  class="w-5 h-5 text-gray-600"
                                                />-->
                        <img
                          :src="getClearbitLogoUrl(file.url)"
                          :alt="file.name || 'Website'"
                          class="w-8 h-8 object-contain absolute inset-0 m-auto z-10"
                          @error="$event.target.style.display='none'"
                        />
                      </div>
                      <div class="flex-1 min-w-0">
                        <div class="tw-flex items-center gap-2 mb-1">
                          <h4 class="text-sm font-medium text-gray-900 truncate">
                            {{ file.name || $t('knowledge_base_external_link') }}</h4>
                          <span
                            v-if="file.is_featured"
                            class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 flex-shrink-0"
                          >
                            {{ $t('knowledge_base_featured') }}
                          </span>
                        </div>
                        <p class="text-xs text-gray-500">{{ getDomainFromUrl(file.url) }}</p>
                      </div>
                    </div>
                    <a
                      :href="file.url"
                      target="_blank"
                      class="tw-flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-800"
                    >
                      <Icon name="external-link" class="w-4 h-4"/>
                      <span>{{ $t('knowledge_base_visit_website') }}</span>
                    </a>
                  </div>

                  <!-- Image Files with Preview -->
                  <div
                    v-else-if="isImageFile(file)"
                    class="tw-flex items-start space-x-3 p-3 rounded-lg border hover:bg-gray-50 transition-colors cursor-pointer"
                    @click="openFile(file)"
                  >
                    <!-- Image Thumbnail -->
                    <div class="tw-flex-shrink-0">
                      <div class="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 relative">
                        <div class="absolute z-2 bg-transparent w-full h-full"></div>

                        <img
                          :src="file.url"
                          :alt="file.name"
                          class="w-full h-full object-cover"
                          @error="handleImageError"
                        />
                      </div>
                    </div>

                    <!-- File Info -->
                    <div class="tw-flex-1 min-w-0">
                      <div class="tw-flex items-center justify-between">
                        <div class="tw-flex items-center gap-2 flex-1 min-w-0">
                          <h4 class="text-sm font-medium text-gray-900 truncate">{{ file.name }}</h4>
                          <span
                            v-if="file.is_featured"
                            class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 flex-shrink-0"
                          >
                            {{ $t('knowledge_base_featured') }}
                          </span>
                        </div>
                        <span v-if="file.size" class="text-xs text-gray-500 tw-flex-shrink-0 ml-2">{{
                            file.size
                          }}</span>
                      </div>

                      <p v-if="file.description" class="text-xs text-gray-600 mt-1 line-clamp-2">
                        {{ file.description }}
                      </p>

                      <div class="tw-flex items-center mt-2 space-x-2">
                        <span class="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          {{ getFileTypeLabel(file.type) }}
                        </span>
                      </div>
                    </div>
                  </div>

                  <!-- Regular Files -->
                  <div v-else>
                    <FilePreview :file="file" @click="openFile(file)"/>
                  </div>
                </div>

                <div v-if="files.length === 0" class="text-center py-8 text-gray-500">
                  <Icon name="document" class="w-12 h-12 mx-auto mb-2 text-gray-300"/>
                  <p class="text-sm">{{ $t('knowledge_base_no_files_attached_detail') }}</p>
                </div>
              </div>
            </div>
            <!-- Actions -->
            <div class="tw-flex flex-col gap-4">
              <div class="bg-white rounded shadow-sm border">
                <div class="p-4 space-y-2">
                  <ds-button
                    v-if="canUserEdit"
                    @click="editDocument"
                    :label="$t('knowledge_base_resource_actions_blog_EDIT')"
                    icon="edit"
                    variant="primary"
                    size="small"
                    class="w-full"
                  />
                  <!-- Delete Button (only for creators) -->
                  <ds-button
                    v-if="canUserEdit"
                    @click="deleteDocument"
                    :label="$t('knowledge_base_resource_actions_blog_DELETE')"
                    icon="trash"
                    variant="danger"
                    size="small"
                    class="w-full"
                  />
                  <BookmarkButton
                    class="w-full"
                    v-if="$store.getters.isLoggedIn"
                    :post-id="article.id"
                    :is-bookmarked="isBookmarked"
                    :bookmarks-count="bookmarksCount"
                    :show-label="true"
                    :show-count="false"
                    variant="default"
                    @bookmarkToggled="handleBookmarkToggled"
                  />
                  <ds-button
                    @click="shareDocument"
                    :label="shareLabel"
                    icon="share"
                    variant="secondary"
                    size="small"
                    class="w-full"
                  />
                  <ds-button
                    v-if="article.downloadUrl"
                    @click="downloadDocument"
                    :label="$t('knowledge_base_download')"
                    icon="download"
                    variant="secondary"
                    size="small"
                    class="w-full"
                  />
                </div>
              </div>

              <!-- Contributors -->
              <div v-if="article.author || contributors.length > 0 || communityManagers.length > 0 || canManageContributors" class="bg-white rounded shadow-sm border">
                <div class="p-4 border-b tw-flex items-center justify-between">
                  <h3 class="text-xs font-medium text-gray-500 uppercase">{{ $t('knowledge_base_contributors') }}</h3>
                  <button
                    v-if="canManageContributors"
                    type="button"
                    class="text-xs text-primary-600 hover:text-primary-700 font-medium"
                    @click="showContributorsModal = true"
                  >
                    {{ $t('knowledge_base_manage_contributors') }}
                  </button>
                </div>
                <div class="p-4 space-y-2">
                  <div
                    v-for="person in [article.author, ...contributors, ...communityManagers].filter((p, i, arr) => p && arr.findIndex(x => x?.id === p.id) === i)"
                    :key="person.id"
                    class="tw-flex items-center gap-2"
                  >
                    <AvatarNew :user="person" show-details/>
                  </div>
                </div>
              </div>

              <!-- Metadata -->
              <div class="bg-white rounded shadow-sm border">
                <div class="p-4 border-b">
                  <h3 class="text-base font-semibold text-gray-900 uppercase">{{ $t('knowledge_base_details') }}</h3>
                </div>
                <div class="p-4 space-y-4">
                  <div class="space-y-2">
                    <div class="tw-flex justify-between text-sm">
                      <span class="text-gray-600">{{ $t('knowledge_base_created') }}</span>
                      <span class="text-gray-900">{{ formatDate(article.created_at) }}</span>
                    </div>
                    <div class="tw-flex justify-between text-sm">
                      <span class="text-gray-600">{{ $t('knowledge_base_updated_at') }}</span>
                      <span class="text-gray-900">{{ formatDate(article.updated_at) }}</span>
                    </div>
                    <div v-if="article.category" class="tw-flex justify-between text-sm">
                      <span class="text-gray-600">{{ $t('knowledge_base_category_label') }}</span>
                      <span class="text-gray-900">{{ article.category }}</span>
                    </div>
                    <div class="tw-flex justify-between text-sm" v-if="accessibleCommunities.length > 0">
                      <span class="text-gray-600">{{ $t('knowledge_base_community') }}</span>
                      <div class="tw-flex flex-wrap gap-1 justify-end">
                        <router-link
                          v-for="community in accessibleCommunities"
                          :key="community.id"
                          class="text-primary text-xs no-underline bg-gray-100 px-2 py-1 rounded"
                          :to="`/communities/${community.id}`"
                        >
                          {{ community.name }}
                        </router-link>
                      </div>
                    </div>

                    <div class="tw-flex justify-between text-sm" v-if="article.actors && article.actors.length > 0">
                      <span class="text-gray-600">{{ $t('knowledge_base_linked_actors') }}</span>
                      <div class="tw-flex flex-wrap gap-1 justify-end">
                        <router-link
                          v-for="actor in article.actors"
                          :key="actor.id"
                          class="text-primary text-xs no-underline bg-gray-100 px-2 py-1 rounded tw-flex items-center gap-1"
                          :to="`/actors/${actor.id}`"
                        >
                          <ActorAvatar :actor="actor" :width="16" variant="rounded" class="rounded-full overflow-hidden"/>
                          {{ actor.name }}
                        </router-link>
                      </div>
                    </div>

                    <div class="tw-flex justify-between text-sm" v-if="accessibleSpottingAreas && accessibleSpottingAreas.length > 0">
                      <span class="text-gray-600">{{ spottingAreaLabel }}</span>
                      <div class="tw-flex flex-wrap gap-1 justify-end">
                        <router-link
                          v-for="spottingArea in accessibleSpottingAreas"
                          :key="spottingArea.id"
                          class="text-primary text-xs no-underline bg-gray-100 px-2 py-1 rounded"
                          :to="`/spotting-areas/${spottingArea.id}`"
                        >
                          {{ spottingArea.name }}
                        </router-link>
                      </div>
                    </div>

                    <div v-if="article.views" class="tw-flex justify-between text-sm">
                      <span class="text-gray-600">{{ $t('knowledge_base_views') }}</span>
                      <span class="text-gray-900">{{ formatNumber(article.views) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
        <div v-else class="text-center py-8">
          <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="lock" class="w-8 h-8 text-gray-400"/>
          </div>
          <p class="text-gray-500 max-w-md mx-auto mb-4">
            {{ !isLoggedIn ? $t('knowledge_base_login_to_view_files') : $t('knowledge_base_no_access') }}
          </p>
          <ds-button
            v-if="!isLoggedIn"
            @click="redirectToLogin"
            :label="$t('knowledge_base_login_button')"
            variant="rounded"
            size="small"
          />
        </div>
      </div>
    </div>
  </div>

  <KnowledgeBaseContributorsModal
    v-if="showContributorsModal && article"
    :post-id="article.id"
    :contributors="contributors"
    :community-managers="communityManagers"
    :can-manage="canManageContributors"
    :community-id="communityId ? Number(communityId) : null"
    :creator="article.author"
    :current-user-id="Number($store.getters.userId)"
    @close="showContributorsModal = false"
    @contributors-updated="loadEditPermissions(article.id)"
  />
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import Icon from '@/components/Icon/Icon.vue'
  import FilePreview from './FilePreview.vue'
  import FilePreviewModal from '@/components/Modals/FilePreviewModal.vue'
  import BookmarkButton from './BookmarkButton.vue'
  import TranslationsMixin from '@/util/TranslationsMixin'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '@/store/modules/ui'
  import MODAL_IDS from '@/constants/modal-ids.js'
  import AvatarNew from '@/components/Avatar/AvatarNew.vue'
  import LoadingNew from '@/components/Loaders/LoadingNew.vue'
  import { fetchPost } from '@/Domain/KnowledgeBase/Api/knowledgeBase'
  import { fetchContributors } from '@/api/knowledge-base'
  import KnowledgeBaseContributorsModal from './KnowledgeBaseContributorsModal.vue'
  import ActorAvatar from '@/Domain/Actor/Components/ActorAvatar.vue'
  import UserMixin from '@/util/UserMixin'
  import { MATOMO_EVENT_ACTIONS, MATOMO_EVENT_CATEGORIES } from '@/constants/analytics-constants'
  import { trackMatomoEvent } from '@/util/analytics'

  export default defineComponent({
    name: 'KnowledgeBaseDetail',
    mixins: [TranslationsMixin, UserMixin],
    components: {
      LoadingNew,
      AvatarNew,
      Icon,
      FilePreview,
      FilePreviewModal,
      BookmarkButton,
      KnowledgeBaseContributorsModal,
      ActorAvatar,
    },
    props: {},
    data() {
      return {
        article: null,
        loading: true,
        error: null,
        relatedArticles: [],
        heroImage: '',
        showFilePreviewModal: false,
        contributorIds: [] as number[],
        contributors: [] as any[],
        communityManagers: [] as any[],
        isCommunityManagerOfPost: false,
        showContributorsModal: false,
      }
    },
    computed: {
      shareLabel() {
        return this.$t('knowledge_base_resource_actions_blog_SHARE')
      },
      spottingAreaId(): Number | null {
        if (this.$route.params.spottingAreaId) {
          return Number(this.$route.params.spottingAreaId)
        }

        return null
      },
      communityId(): Number | null {
        if (this.$route.params.communityId) {
          return Number(this.$route.params.communityId)
        }

        return null
      },
      postId() {
        return Number(this.$route.params.documentId)
      },
      files() {
        if (!this.article || !this.article.files) return []

        return this
          .article
          .files
          .map((file, index) => ({
            id: file.id || `file-${index}`,
            name: file.title || file.originalName || `Document ${index + 1}`,
            type: this.getFileTypeFromMimeType(file.mimetype) || 'unknown',
            url: this.getFileUrlFromItem(file),
            size: this.formatFileSize(file.size),
            description: file.description || file.title || '',
            is_featured: file.isFeatured || false
          }))
      },
      canUserEdit(): boolean {
        if (!this.article || !this.$store.getters.isLoggedIn) {
          return false
        }

        const currentUserId = Number(this.$store.getters.userId)
        const creatorId = this.article.author?.id || this.article.creator?.id || this.article.creator_id

        if (currentUserId === creatorId) return true
        if (this.$store.getters.isOwner || this.$store.getters.isMember) return true
        if (this.contributorIds.includes(currentUserId)) return true
        if (this.isCommunityManagerOfPost) return true

        return false
      },

      canManageContributors(): boolean {
        if (!this.article || !this.$store.getters.isLoggedIn) return false
        const currentUserId = Number(this.$store.getters.userId)
        const creatorId = this.article.author?.id || this.article.creator?.id || this.article.creator_id
        if (currentUserId === creatorId) return true
        if (this.$store.getters.isOwner) return true
        if (this.isCommunityManagerOfPost) return true
        return false
      },

      isBookmarked(): boolean {
        return this.article ? (this.$store.getters.isPostBookmarked(this.article.id) || this.article.isBookmarked || false) : false
      },

      bookmarksCount(): Number {
        return this.article ? (this.$store.getters.getPostBookmarksCount(this.article.id) || this.article.bookmarksCount || 0) : 0
      },
      canUploadContentToKnowledgeBase() {
        return this.$store.getters.canUploadContentToKnowledgeBase
      },
      shouldCurateContentToKnowledgeBase() {
        return this.$store.getters.shouldCurateContentToKnowledgeBase
      },
      canViewAllKnowledgeBaseContentTypes() {
        if (this.canUserEdit) {
          return true
        }

        return this.$store.getters.canViewAllKnowledgeBaseContentTypes || this.canPostBeAccessedByPortfolioMember
      },
      canViewPreviewKnowledgeBaseContentTypes() {
        return this.$store.getters.canViewPreviewKnowledgeBaseContentTypes || this.canPostBeAccessedByPortfolioMember
      },
      canViewDetailKnowledgeBaseContentTypes() {
        if (this.canUserEdit) {
          return true
        }

        return this.$store.getters.canViewDetailKnowledgeBaseContentTypes || this.canPostBeAccessedByPortfolioMember
      },
      canPostBeAccessedByPortfolioMember(): boolean {
        if (this.canUserEdit) {
          return true
        }

        if (this.$store.getters.userRole === 'portfolio_member' && this.article.spottingAreas?.length > 0) {
          const contributableIds = this.$store.getters.contributeSpottingAreaIds || []

          return this.article.spottingAreas.map(s => s.id).some(id => contributableIds.includes(Number(id)))
        }

        return false
      },
      accessibleSpottingAreas() {
        if (!this.article || !this.article.spottingAreas) return []

        // Filter spotting areas to only show those the user has access to
        return this.article.spottingAreas.filter(spottingArea => !!spottingArea.user_has_access)
      },
      accessibleCommunities() {
        if (!this.article || !this.article.communities) return []

        // Filter communities to only show those the user has access to
        const accessibleCommunityIds = this.$store.state.communities.listData.data.map((c => c.id))

        return this.article.communities.filter(community => accessibleCommunityIds.includes(community.id))
      },
    },
    created() {
      // Preferably, we don't register listeners in an async hook (mounted is async in this component)
      this.$bus.on('knowledgeBasePostDeleteConfirmation', this.handleConfirmAction)
    },
    async mounted() {
      await this.loadKnowledgeBasePost()
    },
    unmounted() {
      this.$bus.off('knowledgeBasePostDeleteConfirmation', this.handleConfirmAction)
    },
    methods: {
      goBackTo() {
        if (this.$route.params.spottingAreaId) {
          this.$router.push(`/spotting-areas/${this.$route.params.spottingAreaId}/knowledge-base`)
        }

        if (this.$route.params.communityId) {
          if (this.$route.query.curation && this.$route.query.curation === 'knowledge_base') {
            return this.$router.push(`/communities/${this.$route.params.communityId}/curation?curation=knowledge_base`)
          }

          this.$router.push(`/communities/${this.$route.params.communityId}/knowledge-base`)
        }

        return this.$router.push('/dashboards/knowledge-base')
      },
      async loadKnowledgeBasePost() {
        try {
          this.loading = true
          this.error = null

          await fetchPost(this.postId)
            .then((response) => {
              const postData = response

              if (!postData) {
                this.error = this.$t('knowledge_base_document_not_found')
                return
              }

              // Transform structured domain service response to article format
              this.article = this.transformDomainPostToArticle(postData)

              // Load bookmark status for this post
              if (this.$store.getters.isLoggedIn) {
                this.$store.dispatch('checkBookmark', { postId: this.article.id })
                this.loadEditPermissions(this.article.id)
              }

              // TODO: Load related articles from API if needed
              this.relatedArticles = []
            })


        } catch (error) {
          console.error('Error loading article:', error)
          this.error = this.$t('knowledge_base_failed_to_load_document')
        } finally {
          this.loading = false
        }
      },
      async loadEditPermissions(postId: number) {
        try {
          const currentUserId = Number(this.$store.getters.userId)
          const data = await fetchContributors(postId)

          this.contributors = data.contributors || []
          this.contributorIds = this.contributors.map((c: any) => Number(c.id))
          this.communityManagers = data.community_managers || []

          this.isCommunityManagerOfPost = (data.community_managers || [])
            .some((m: any) => Number(m.id) === currentUserId)
        } catch (e) {
          // Non-critical — edit button stays hidden if this fails
        }
      },
      goBack() {
        if (this.communityId) {
          this.$router.push(`/communities/${this.communityId}/knowledge-base`)
          return
        }

        if (this.spottingAreaId) {
          this.$router.push(`/spotting-areas/${this.spottingAreaId}/knowledge-base`)
          return
        }

        this.$router.push('/dashboards/knowledge-base')
      },
      handleBookmarkToggled(data) {
        // Update the store state
        this.$store.dispatch('updatePostBookmarkStatus', data)
        // Update local article state for immediate UI feedback
        if (this.article) {
          this.article.isBookmarked = data.isBookmarked
          this.article.bookmarksCount = data.bookmarksCount
        }
      },
      shareDocument() {
        // Fallback to copying URL
        navigator.clipboard.writeText(window.location.href)

        this.shareLabel = this.$t('knowledge_base_resource_actions_blog_SHARED')

        setTimeout(() => {
          this.shareLabel = this.$t('knowledge_base_resource_actions_blog_SHARE')
        }, 5000)
      },
      downloadDocument() {
        if (this.article.downloadUrl) {
          window.open(this.article.downloadUrl, '_blank')
        }
      },
      openFile(file) {
        // Skip modal for embedded content (YouTube/Spotify) as they're already embedded
        if (this.isYouTubeUrl(file.url) || this.isSpotifyUrl(file.url)) {
          return
        }

        // For regular URLs, open in new tab
        if (this.isRegularUrl(file)) {
          window.open(file.url, '_blank')
          return
        }

        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, {
          fileUrl: file.url,
          fileName: file.name,
          fileType: file.type,
          fileId: file.id
        })
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.FILE_PREVIEW)

        // For files, show preview modal
        this.showFilePreviewModal = true
      },
      editDocument() {
        // Navigate to edit page instead of showing modal
        if (this.communityId) {
          this.$router.push(`/communities/${this.communityId}/knowledge-base/posts/${this.article.id}/edit`)
        } else if (this.spottingAreaId) {
          this.$router.push(`/spotting-areas/${this.spottingAreaId}/knowledge-base/posts/${this.article.id}/edit`)
        } else {
          this.$router.push(`/knowledge-base/posts/${this.article.id}/edit`)
        }
      },
      deleteDocument() {
        // Set up the confirmation modal context
        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, {
          title: this.article.title,
          body: this.$t('knowledge_base_delete_post_confirm'),
          confirmLabel: this.$t('delete'),
          cancelLabel: this.$t('cancel'),
          isCancellable: true,
          modalContextType: 'knowledge-base-post',
          postId: this.article.id,
          communityId: this.communityId
        })

        // Show the confirmation modal
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.DELETE_CONFIRMATION)
      },
      handleConfirmAction(context) {
        // Handle different confirmation actions based on actionType
        if (context && context.modalContextType === 'knowledge-base-post') {
          this.confirmDeleteDocument()
        }
      },
      async confirmDeleteDocument() {
        try {
          // Import deletePost function from the knowledge base API
          const { deletePost } = await import('@/Domain/KnowledgeBase/Api/knowledgeBase')
          await deletePost(this.article.id, this.communityId)

          trackMatomoEvent(MATOMO_EVENT_CATEGORIES.KNOWLEDGE_BASE, MATOMO_EVENT_ACTIONS.DELETE_KNOWLEDGE_BASE_RESOURCE, this.article.id)

          // Navigate back to knowledge base overview
          if (this.communityId) {
            this.$router.push(`/communities/${this.communityId}/knowledge-base`)
          } else {
            this.$router.push('/dashboards/knowledge-base')
          }
        } catch (error) {
          console.error('Failed to delete post:', error)
        }
      },
      isAccessibleForUser() {
        return true // Mock function
      },
      formatDate(date) {
        return new Date(date).toLocaleDateString(this.$store.getters.activeLocale, {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      },
      formatNumber(num) {
        return num?.toLocaleString() || '0'
      },
      formatFileSize(bytes) {
        if (!bytes || bytes === 0) return '0 Bytes'
        const k = 1024
        const sizes = ['Bytes', 'KB', 'MB', 'GB']
        const i = Math.floor(Math.log(bytes) / Math.log(k))
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
      },
      getFileTypeLabel(fileType) {
        const labelMap = {
          pdf: 'PDF',
          excel: 'Excel',
          xls: 'Excel',
          xlsx: 'Excel',
          word: 'Word',
          doc: 'Word',
          docx: 'Word',
          powerpoint: 'PowerPoint',
          ppt: 'PowerPoint',
          pptx: 'PowerPoint',
          image: 'Image',
          jpg: 'Image',
          jpeg: 'Image',
          png: 'Image',
          gif: 'Image',
          mp3: 'Audio',
          mp4: 'Video',
          youtube: 'YouTube Video',
          spotify: 'Spotify',
          text: 'Text',
          txt: 'Text',
          url: 'Link'
        }
        return labelMap[fileType?.toLowerCase()] || this.$t('knowledge_base_file_type_generic')
      },
      transformDomainPostToArticle(postData) {
        // Transform domain service structured data to article format
        return {
          id: postData.id,
          title: postData.title || this.$t('knowledge_base_untitled'),
          body: postData.body || '',
          excerpt: postData.excerpt || '',
          status: postData.isPublic ? 'published' : 'draft',
          visibility: postData.isPublic ? 'public' : 'private',
          views: 0, // Could be added to domain later
          likes: 0, // Could be added to domain later
          created_at: postData.createdAt,
          updated_at: postData.updatedAt,
          featured_image: postData.thumbnail,
          thumbnail_auto_generated: postData.thumbnailAutoGenerated,
          author: postData.creator,
          creator_id: postData.creatorId || postData.creator?.id,
          category: postData.category?.name,
          categoryId: postData.category?.id || postData.knowledgeBaseCategoryId,
          fileType: this.getFileTypeFromFiles(postData.files),
          fileName: null, // Will be extracted from files if needed
          fileUrl: null, // Will be extracted from files if needed
          fileSize: null, // Will be extracted from files if needed
          isBookmarked: false, // Could be added to domain later
          downloadUrl: null, // Will be extracted from files if needed
          files: postData.files || [],
          communities: postData.communities || [],
          spottingAreas: postData.spottingAreas || [],
          actors: postData.actors || [],
        }
      },

      getFileTypeFromMimeType(mimeType) {
        if (!mimeType) return 'unknown'

        const mimeTypeMap = {
          'image/jpeg': 'jpg',
          'image/png': 'png',
          'image/gif': 'gif',
          'image/webp': 'webp',
          'image/svg+xml': 'svg',
          'application/pdf': 'pdf',
          'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
          'application/vnd.ms-powerpoint': 'ppt',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
          'application/vnd.ms-excel': 'xls',
          'application/msword': 'doc',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
          'audio/mpeg': 'mp3',
          'audio/mp3': 'mp3',
          'video/mp4': 'mp4',
          'text/csv': 'csv',
          'text/plain': 'txt',
          'application/zip': 'zip',
          'link': 'url' // For URL links
        }

        return mimeTypeMap[mimeType] || 'unknown'
      },
      getFileUrlFromItem(item) {
        // Check if it's a direct URL link in the url field
        if (item.url && (item.url.startsWith('http://') || item.url.startsWith('https://'))) {
          return item.url
        }

        // Check if it's a direct URL link in the path field
        if (item.path && (item.path.startsWith('http://') || item.path.startsWith('https://'))) {
          return item.path
        }

        // Check if it's a URL link in meta
        if (item && Array.isArray(item.meta)) {
          const urlMeta = item.meta.find(meta => meta.key === 'url')
          if (urlMeta?.value && urlMeta.value.trim()) {
            return urlMeta.value
          }
        }

        // For file uploads, use the sql_media_id to construct the API URL
        if (item.id) {
          return `/api/files/${item.id}`
        }

        return null
      },
      getFileNameFromTitle(title, fileType) {
        if (!title) return `document.${fileType}`

        // Clean title for filename
        const cleanTitle = title.replace(/[^a-zA-Z0-9\s-]/g, '').replace(/\s+/g, '-').toLowerCase()
        return `${cleanTitle}.${fileType}`
      },
      formatUnixTimestamp(timestamp) {
        if (!timestamp) return new Date().toISOString()
        return new Date(timestamp * 1000).toISOString()
      },
      isImageType(fileType) {
        return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(fileType?.toLowerCase())
      },
      estimateFileSize(fileType) {
        // Mock file size estimation
        const sizeMap = {
          pdf: 2400000, // 2.4MB
          image: 856000, // 856KB
          excel: 1200000, // 1.2MB
          mp3: 45200000, // 45.2MB
          powerpoint: 8700000 // 8.7MB
        }
        return sizeMap[fileType] || 1000000 // 1MB default
      },
      checkIfBookmarked(likedBy) {
        // Mock bookmark checking
        return false
      },
      getFileTypeFromFiles(files) {
        if (!files || !files.length) return null
        const firstFile = files[0]
        return this.getFileTypeFromMimeType(firstFile.mimetype)
      },

      // YouTube and Spotify helper methods
      isYouTubeUrl(url) {
        if (!url) return false
        return url.includes('youtube.com') || url.includes('youtu.be')
      },

      isSpotifyUrl(url) {
        if (!url) return false
        return url.includes('spotify.com')
      },

      isRegularUrl(file) {
        return file.url && !this.isYouTubeUrl(file.url) && !this.isSpotifyUrl(file.url) && (file.type === 'url' || !file.type)
      },

      getYouTubeEmbedUrl(url) {
        if (url.includes('youtu.be/')) {
          const videoId = url.split('youtu.be/')[1].split('?')[0]
          return `https://www.youtube-nocookie.com/embed/${videoId}`
        } else if (url.includes('youtube.com/watch?v=')) {
          const videoId = url.split('v=')[1].split('&')[0]
          return `https://www.youtube-nocookie.com/embed/${videoId}`
        }
        return ''
      },

      getSpotifyEmbedUrl(url) {
        // Convert Spotify URLs to embed format
        const match = url.match(/spotify\.com\/(track|album|playlist|episode)\/([a-zA-Z0-9]+)/)
        if (match) {
          return `https://open.spotify.com/embed/${match[1]}/${match[2]}`
        }
        return ''
      },

      getClearbitLogoUrl(url) {
        try {
          const domain = new URL(url).hostname.replace('www.', '').replace('open.', '')

          const token = import.meta.env.VITE_LOGO_DEV_PUBLISHABLE_KEY || ''

          return `https://img.logo.dev/${domain}` + (token ? `?token=${token}` : '')
        } catch {
          return ''
        }
      },

      getDomainFromUrl(url) {
        try {
          return new URL(url).hostname.replace('www.', '')
        } catch {
          return url
        }
      },

      formatTime(seconds) {
        const sec = Math.floor(seconds || 0)
        const hrs = Math.floor(sec / 3600)
        const mins = Math.floor((sec % 3600) / 60)
        const secs = sec % 60
        return hrs > 0
          ? `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
          : `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
      },

      isImageFile(file) {
        if (!file) return false
        const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp']
        const fileType = file.type?.toLowerCase()

        // Check file extension in type field
        if (imageTypes.includes(fileType)) {
          return true
        }

        // Check MIME type
        if (fileType && fileType.startsWith('image/')) {
          return true
        }

        // Check file extension in filename
        const extension = file.name?.split('.').pop()?.toLowerCase()
        return extension && imageTypes.includes(extension)
      },

      handleImageError(event) {
        // Hide the image if it fails to load and show an error message or fallback
        console.warn('Failed to load image:', event.target.src)
        event.target.style.display = 'none'

        // You could also replace with a placeholder image:
        // event.target.src = '/path/to/placeholder-image.png'
      },

      redirectToLogin() {
        // Store the current URL to redirect back after login
        const currentUrl = this.$route.fullPath
        this.$router.push({
          path: '/login',
          query: { redirect: currentUrl }
        })
      }
    },
    watch: {
      postId() {
        this.loadKnowledgeBasePost()
      }
    }
  })
</script>

<style scoped>
  .prose {
    color: #374151;
  }

  .prose h1 {
    border-bottom: 2px solid #e5e7eb;
    padding-bottom: 0.5rem;
  }

  .prose h2 {
    border-bottom: 1px solid #e5e7eb;
    padding-bottom: 0.25rem;
  }

  .prose code {
    background-color: #f3f4f6;
    padding: 0.125rem 0.25rem;
    border-radius: 0.25rem;
    font-size: 0.875rem;
  }

  .prose pre {
    background-color: #1f2937;
    color: #f9fafb;
    overflow-x: auto;
  }

  .prose pre code {
    background-color: transparent;
    color: inherit;
    padding: 0;
  }

  .line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
</style>
